import React, { useState, useRef } from 'react';

const EducationCalculatorSection = () => {
  const [childAgeToday, setChildAgeToday] = useState(8);
  const [joinCollegeAge, setJoinCollegeAge] = useState(18);
  const [educationDuration, setEducationDuration] = useState(5);
  const [currentCostPerYear, setCurrentCostPerYear] = useState(200000);
  const [existingSavings, setExistingSavings] = useState(0);
  const [expectedReturn, setExpectedReturn] = useState(12);
  const [inflationRate, setInflationRate] = useState(6);
  const [result, setResult] = useState(null);
  const resultRef = useRef(null);

  const calculateEducation = () => {
    const yearsToCollege = joinCollegeAge - childAgeToday;
    const totalEducationYears = educationDuration;
    
    // Calculate future cost of education considering inflation
    const inflationFactor = Math.pow(1 + inflationRate / 100, yearsToCollege);
    const futureCostFirstYear = currentCostPerYear * inflationFactor;
    
    // Calculate total cost over education duration (with inflation during college)
    let totalEducationCost = 0;
    for (let year = 0; year < totalEducationYears; year++) {
      const yearInflationFactor = Math.pow(1 + inflationRate / 100, year);
      totalEducationCost += futureCostFirstYear * yearInflationFactor;
    }
    
    // Calculate future value of existing savings
    const futureValueExistingSavings = existingSavings * Math.pow(1 + expectedReturn / 100, yearsToCollege);
    
    // Calculate shortfall
    const shortfall = totalEducationCost - futureValueExistingSavings;
    
    // Calculate monthly SIP required to cover shortfall
    const monthlyRate = expectedReturn / 100 / 12;
    const months = yearsToCollege * 12;
    
    let monthlySIPRequired = 0;
    let lumpSumRequired = 0;
    
    if (shortfall > 0 && months > 0) {
      // Monthly SIP calculation
      monthlySIPRequired = shortfall * monthlyRate / (Math.pow(1 + monthlyRate, months) - 1);
      
      // Lump sum calculation (present value of shortfall)
      lumpSumRequired = shortfall / Math.pow(1 + expectedReturn / 100, yearsToCollege);
    }
    
    setResult({
      totalEducationCost: Math.round(totalEducationCost),
      futureValueExistingSavings: Math.round(futureValueExistingSavings),
      shortfall: Math.round(Math.max(0, shortfall)),
      monthlySIPRequired: Math.round(Math.max(0, monthlySIPRequired)),
      lumpSumRequired: Math.round(Math.max(0, lumpSumRequired)),
      yearsToCollege
    });

    // Scroll to results section
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const styles = {
    educationCalculatorContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem'
    },
    description: {
      color: '#6b7280',
      fontSize: '1rem',
      lineHeight: '1.6',
      margin: '0 0 2rem 0'
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    label: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: '#374151',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    valueDisplay: {
      backgroundColor: '#374151',
      color: 'white',
      padding: '0.25rem 0.75rem',
      borderRadius: '15px',
      fontSize: '0.85rem',
      fontWeight: '600'
    },
    manualInput: {
      padding: '0.5rem 0.75rem',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#374151',
      backgroundColor: 'white',
      width: '120px',
      textAlign: 'center',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    },
    sliderContainer: {
      position: 'relative',
      margin: '1rem 0'
    },
    slider: {
      width: '100%',
      height: '6px',
      borderRadius: '3px',
      background: '#e5e7eb',
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none'
    },
    sliderLabels: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.75rem',
      color: '#9ca3af',
      marginTop: '0.5rem'
    },
    calculateButton: {
      backgroundColor: '#22c55e',
      color: 'white',
      border: 'none',
      padding: '0.75rem 2rem',
      borderRadius: '25px',
      fontSize: '1rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      alignSelf: 'center',
      marginTop: '1rem'
    },
    resultSection: {
      marginTop: '2rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '15px',
      border: '1px solid #e5e7eb',
      overflow: 'hidden'
    },
    resultHeader: {
      backgroundColor: '#bfdbfe',
      padding: '1rem 1.5rem',
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#1e40af',
      borderBottom: '1px solid #e5e7eb'
    },
    resultTable: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    resultRow: {
      display: 'flex',
      borderBottom: '1px solid #e5e7eb'
    },
    resultLabel: {
      flex: '1',
      padding: '1rem 1.5rem',
      backgroundColor: '#f8f9fa',
      fontSize: '0.95rem',
      fontWeight: '500',
      color: '#374151',
      borderRight: '1px solid #e5e7eb'
    },
    resultValue: {
      flex: '1',
      padding: '1rem 1.5rem',
      backgroundColor: '#ffffff',
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1a1a1a',
      textAlign: 'right'
    },
    investmentOptions: {
      backgroundColor: '#ffffff',
      padding: '1rem 1.5rem',
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1a1a1a',
      textAlign: 'right'
    },
    recalculateButton: {
      backgroundColor: '#6b7280',
      color: 'white',
      border: '1px solid #d1d5db',
      padding: '0.6rem 1.5rem',
      borderRadius: '6px',
      fontSize: '0.9rem',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
      margin: '1.5rem'
    }
  };

  return (
    <>
      <style>{`
        .education-slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .education-slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .education-calculate-btn:hover {
          background-color:rgb(220, 38, 56) !important;
        }
        
        .recalculate-btn:hover {
          background-color: #4b5563 !important;
        }
        
        .manual-input:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
        }
        
        @media (max-width: 768px) {
          .result-row {
            flex-direction: column !important;
          }
          .result-label, .result-value {
            border-right: none !important;
            border-bottom: 1px solid #e5e7eb !important;
          }
        }
      `}</style>
      
      <div style={styles.educationCalculatorContent}>
        <p style={styles.description}>
          It helps you plan for the future expenditure for your child's higher education
        </p>
        
        {/* Child's Age Today */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Child's age today (Years)
            <input
              type="number"
              min="0"
              max="25"
              step="1"
              value={childAgeToday}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setChildAgeToday('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setChildAgeToday(Math.max(0, Math.min(25, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setChildAgeToday(8);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter age"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="25"
              step="1"
              value={childAgeToday || 8}
              onChange={(e) => setChildAgeToday(parseInt(e.target.value))}
              style={styles.slider}
              className="education-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>5</span>
              <span>10</span>
              <span>15</span>
              <span>20</span>
              <span>25</span>
            </div>
          </div>
        </div>

        {/* Join College Age */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Join College at age (Years)
            <input
              type="number"
              min="16"
              max="25"
              step="1"
              value={joinCollegeAge}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setJoinCollegeAge('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setJoinCollegeAge(Math.max(16, Math.min(25, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setJoinCollegeAge(18);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter age"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="16"
              max="25"
              step="1"
              value={joinCollegeAge || 18}
              onChange={(e) => setJoinCollegeAge(parseInt(e.target.value))}
              style={styles.slider}
              className="education-slider"
            />
            <div style={styles.sliderLabels}>
              <span>16</span>
              <span>17</span>
              <span>18</span>
              <span>19</span>
              <span>20</span>
              <span>21</span>
              <span>22</span>
              <span>23</span>
              <span>24</span>
              <span>25</span>
            </div>
          </div>
        </div>

        {/* Duration of Education */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Duration of education (years)
            <input
              type="number"
              min="1"
              max="20"
              step="1"
              value={educationDuration}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setEducationDuration('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setEducationDuration(Math.max(1, Math.min(20, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setEducationDuration(5);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter years"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="1"
              max="20"
              step="1"
              value={educationDuration || 5}
              onChange={(e) => setEducationDuration(parseInt(e.target.value))}
              style={styles.slider}
              className="education-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1</span>
              <span>3</span>
              <span>5</span>
              <span>7</span>
              <span>9</span>
              <span>11</span>
              <span>13</span>
              <span>15</span>
              <span>17</span>
              <span>20</span>
            </div>
          </div>
        </div>

        {/* Current Cost Per Year */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Approx Current Cost per year (Rs.)
            <input
              type="number"
              min="100000"
              max="10000000"
              step="50000"
              value={currentCostPerYear}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setCurrentCostPerYear('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setCurrentCostPerYear(Math.max(100000, Math.min(10000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setCurrentCostPerYear(200000);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter cost"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="100000"
              max="10000000"
              step="50000"
              value={currentCostPerYear || 200000}
              onChange={(e) => setCurrentCostPerYear(parseInt(e.target.value))}
              style={styles.slider}
              className="education-slider"
            />
            <div style={styles.sliderLabels}>
              <span>1L</span>
              <span>25L</span>
              <span>50L</span>
              <span>75L</span>
              <span>1Cr</span>
            </div>
          </div>
        </div>

        {/* Existing Savings */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Any savings for Education already done ?(Rs.)
            <input
              type="number"
              min="0"
              max="10000000"
              step="100000"
              value={existingSavings}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setExistingSavings('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setExistingSavings(Math.max(0, Math.min(10000000, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setExistingSavings(0);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter savings"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="10000000"
              step="100000"
              value={existingSavings || 0}
              onChange={(e) => setExistingSavings(parseInt(e.target.value))}
              style={styles.slider}
              className="education-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>25L</span>
              <span>50L</span>
              <span>75L</span>
              <span>1Cr</span>
            </div>
          </div>
        </div>

        {/* Expected Rate of Return */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Expected rate of return (%)
            <input
              type="number"
              min="6"
              max="30"
              step="1"
              value={expectedReturn}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setExpectedReturn('');
                } else {
                  const numValue = parseInt(inputValue);
                  if (!isNaN(numValue)) {
                    setExpectedReturn(Math.max(6, Math.min(30, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseInt(e.target.value))) {
                  setExpectedReturn(12);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="6"
              max="30"
              step="1"
              value={expectedReturn || 12}
              onChange={(e) => setExpectedReturn(parseInt(e.target.value))}
              style={styles.slider}
              className="education-slider"
            />
            <div style={styles.sliderLabels}>
              <span>6</span>
              <span>9</span>
              <span>12</span>
              <span>15</span>
              <span>18</span>
              <span>21</span>
              <span>24</span>
              <span>27</span>
              <span>30</span>
            </div>
          </div>
        </div>

        {/* Expected Inflation Rate */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>
            Expected Inflation rate (%)
            <input
              type="number"
              min="0"
              max="15"
              step="0.5"
              value={inflationRate}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === '') {
                  setInflationRate('');
                } else {
                  const numValue = parseFloat(inputValue);
                  if (!isNaN(numValue)) {
                    setInflationRate(Math.max(0, Math.min(15, numValue)));
                  }
                }
              }}
              onBlur={(e) => {
                if (e.target.value === '' || isNaN(parseFloat(e.target.value))) {
                  setInflationRate(6);
                }
              }}
              style={styles.manualInput}
              className="manual-input"
              placeholder="Enter rate"
            />
          </label>
          <div style={styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="15"
              step="0.5"
              value={inflationRate || 6}
              onChange={(e) => setInflationRate(parseFloat(e.target.value))}
              style={styles.slider}
              className="education-slider"
            />
            <div style={styles.sliderLabels}>
              <span>0</span>
              <span>1.5</span>
              <span>3</span>
              <span>4.5</span>
              <span>6</span>
              <span>7.5</span>
              <span>9</span>
              <span>10.5</span>
              <span>12</span>
              <span>13.5</span>
              <span>15</span>
            </div>
          </div>
        </div>

        {/* Calculate Button */}
        <button
          style={styles.calculateButton}
          className="education-calculate-btn"
          onClick={calculateEducation}
        >
          Calculate
        </button>

        {/* Results */}
        {result && (
          <div ref={resultRef} style={styles.resultSection}>
            <div style={styles.resultHeader}>
              Result
            </div>
            
            <div style={styles.resultRow} className="result-row">
              <div style={styles.resultLabel}>Corpus Required at start of College (Rs.)</div>
              <div style={styles.resultValue}>
                {(result.totalEducationCost / 100000).toFixed(2).replace(/\.?0+$/, '')} L
              </div>
            </div>
            
            <div style={styles.resultRow} className="result-row">
              <div style={styles.resultLabel}>Your existing savings will grow to (Rs.)</div>
              <div style={styles.resultValue}>
                {result.futureValueExistingSavings === 0 ? '0' : 
                 (result.futureValueExistingSavings / 100000).toFixed(2).replace(/\.?0+$/, '') + ' L'}
              </div>
            </div>
            
            <div style={styles.resultRow} className="result-row">
              <div style={styles.resultLabel}>Shortfall to be covered (Rs.)</div>
              <div style={styles.resultValue}>
                {(result.shortfall / 100000).toFixed(2).replace(/\.?0+$/, '')} L
              </div>
            </div>
            
            <div style={styles.resultRow} className="result-row">
              <div style={styles.resultLabel}>To meet this Goal you must Invest (Rs.)</div>
              <div style={styles.investmentOptions}>
                {result.shortfall > 0 ? (
                  <>
                    <div style={{marginBottom: '0.5rem'}}>
                      <strong>{(result.lumpSumRequired / 100000).toFixed(2).replace(/\.?0+$/, '')} L Lumpsum amount</strong>
                    </div>
                    <div style={{fontSize: '0.9rem', color: '#6b7280'}}>or</div>
                    <div style={{marginTop: '0.5rem'}}>
                      <strong>{Math.round(result.monthlySIPRequired).toLocaleString()} Monthly SIP</strong>
                    </div>
                  </>
                ) : (
                  <div style={{color: '#059669'}}>
                    <strong>No additional investment needed!</strong>
                  </div>
                )}
              </div>
            </div>
            
            <button
              style={styles.recalculateButton}
              className="recalculate-btn"
              onClick={calculateEducation}
            >
              Re-Calculate
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default EducationCalculatorSection;